import React, { useState, useMemo } from "react";
import { Database, Cloud, FileSpreadsheet, BarChart2, Mail, Users, ShoppingCart, Megaphone, PlugZap, Box, Utensils, ShoppingBag } from "lucide-react";

/**
 * Shared connector-icon component with graceful multi-stage fallbacks.
 *
 * Source-of-truth precedence:
 *   1. `iconUrl` or `driver.icon_url`
 *   2. S2 Favicon derived from `baseUrl` / `domain` / `driver.base_url` or known key/name
 *   3. category-mapped lucide icon (Database / Cloud / Marketing / etc.)
 *   4. Generic plug icon
 */
const CATEGORY_ICONS = {
    "Database": Database,
    "Data Warehouse": Database,
    "Cloud Storage": Cloud,
    "File": FileSpreadsheet,
    "Files": FileSpreadsheet,
    "Marketing": Megaphone,
    "Ads": Megaphone,
    "Analytics": BarChart2,
    "CRM": Users,
    "E-commerce": ShoppingCart,
    "Email": Mail,
    "Restaurant": Utensils,
    "POS": ShoppingBag,
    "Labor": Users,
    "Custom": Box,
};

const KNOWN_DOMAINS = {
    "onedrive": "onedrive.live.com",
    "microsoft_onedrive": "onedrive.live.com",
    "microsoft_bing_ads": "login.microsoftonline.com",
    "bing_ads": "login.microsoftonline.com",
    "bing": "bing.com",
    "azure": "azure.microsoft.com",
    "azure_blob_storage": "azure.microsoft.com",
    "google_drive": "drive.google.com",
    "google_ads": "ads.google.com",
    "google_sheets": "docs.google.com",
    "ga4": "analytics.google.com",
    "youtube_ads": "youtube.com",
    "meta_ads": "facebook.com",
    "facebook": "facebook.com",
    "instagram": "instagram.com",
    "x_twitter_ads": "x.com",
    "twitter": "x.com",
    "linkedin_ads": "linkedin.com",
    "linkedin": "linkedin.com",
    "shopify": "shopify.com",
    "hubspot_crm": "hubspot.com",
    "hubspot": "hubspot.com",
    "zoho_crm": "zoho.com",
    "zoho": "zoho.com",
    "salesforce": "salesforce.com",
    "s3": "aws.amazon.com",
    "amazon_s3": "aws.amazon.com",
    "snowflake": "snowflake.com",
    "stripe": "stripe.com",
    "quickbooks": "quickbooks.intuit.com",
    "postgresql": "postgresql.org",
    "mysql": "mysql.com",
    "sqlserver": "microsoft.com",
    "toast": "toasttab.com",
    "toast_v3": "toasttab.com",
};

function extractHost(u) {
    if (!u) return "";
    try {
        const parsed = new URL(u.startsWith("http") ? u : `https://${u}`);
        return parsed.hostname.replace(/^www\./, "");
    } catch {
        return "";
    }
}

function inferHostFromNameOrKey(name, key) {
    const k = (key || "").toLowerCase().trim();
    if (k && KNOWN_DOMAINS[k]) return KNOWN_DOMAINS[k];
    const n = (name || "").toLowerCase().trim();
    if (n.includes("onedrive")) return "onedrive.live.com";
    if (n.includes("bing")) return "login.microsoftonline.com";
    if (n.includes("drive") && n.includes("google")) return "drive.google.com";
    if (n.includes("sheets") && n.includes("google")) return "docs.google.com";
    if (n.includes("google")) return "google.com";
    if (n.includes("meta") || n.includes("facebook")) return "facebook.com";
    if (n.includes("twitter") || n.includes("x (")) return "x.com";
    if (n.includes("linkedin")) return "linkedin.com";
    if (n.includes("hubspot")) return "hubspot.com";
    if (n.includes("zoho")) return "zoho.com";
    if (n.includes("salesforce")) return "salesforce.com";
    if (n.includes("snowflake")) return "snowflake.com";
    if (n.includes("shopify")) return "shopify.com";
    if (n.includes("stripe")) return "stripe.com";
    if (n.includes("toast")) return "toasttab.com";
    if (n.includes("azure")) return "azure.microsoft.com";
    if (n.includes("microsoft") || n.includes("sql server")) return "microsoft.com";
    if (n.includes("s3") || n.includes("amazon") || n.includes("aws")) return "aws.amazon.com";
    if (n.includes("postgres")) return "postgresql.org";
    if (n.includes("mysql")) return "mysql.com";
    return "";
}

function inferCategoryFromNameOrKey(name, key, cat) {
    if (cat && CATEGORY_ICONS[cat]) return cat;
    const str = `${name || ""} ${key || ""}`.toLowerCase();
    if (str.includes("drive") || str.includes("storage") || str.includes("blob") || str.includes("s3") || str.includes("sftp") || str.includes("onedrive")) return "Cloud Storage";
    if (str.includes("sheet") || str.includes("csv") || str.includes("excel") || str.includes("file")) return "File";
    if (str.includes("ad") || str.includes("marketing") || str.includes("campaign") || str.includes("analytics") || str.includes("ga4") || str.includes("meta")) return "Marketing";
    if (str.includes("crm") || str.includes("hubspot") || str.includes("salesforce") || str.includes("zoho")) return "CRM";
    if (str.includes("sql") || str.includes("database") || str.includes("postgres") || str.includes("mysql") || str.includes("mongo")) return "Database";
    if (str.includes("toast") || str.includes("restaurant") || str.includes("pos")) return "Restaurant";
    return cat || "Custom";
}

export default function ConnectorIcon({ iconUrl, category, name, className = "", baseUrl, domain, driver, connectorKey }) {
    const [stage, setStage] = useState(0);

    const primaryUrl = iconUrl || driver?.icon_url;
    const key = connectorKey || driver?.key;
    const cat = inferCategoryFromNameOrKey(name, key, category || driver?.category);
    const n = name || driver?.name || "connector";
    const host = useMemo(() => {
        const raw = baseUrl || domain || driver?.base_url || driver?.domain_url || driver?.domain;
        const fromRaw = extractHost(raw);
        if (fromRaw) return fromRaw;
        return inferHostFromNameOrKey(n, key);
    }, [baseUrl, domain, driver, n, key]);

    const s2Url = useMemo(() => {
        if (!host) return null;
        return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=128`;
    }, [host]);

    const Fallback = CATEGORY_ICONS[cat] || PlugZap;

    if (stage === 0 && primaryUrl) {
        return (
            <img
                src={primaryUrl}
                alt={n}
                className={className}
                onError={() => setStage(s2Url ? 1 : 2)}
                loading="lazy"
            />
        );
    }

    if (stage <= 1 && s2Url) {
        return (
            <img
                src={s2Url}
                alt={n}
                className={className}
                onError={() => setStage(2)}
                loading="lazy"
            />
        );
    }

    return <Fallback className={`text-muted-foreground ${className}`} strokeWidth={1.5} aria-label={n} />;
}
